import React, { useState } from 'react';
import { useWallet } from '../../contexts/WalletContext';
import { WALLET_TYPES, CHAINS } from '../../config/constants';
import { formatAddress, formatTokenAmount } from '../../utils/format';
import Button from '../UI/Button';
import {
  WalletIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  DocumentDuplicateIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const WalletConnection = () => {
  const {
    isConnected,
    address,
    chainId,
    balance,
    walletType,
    isLoading,
    connectWallet,
    disconnect,
    getChainInfo,
  } = useWallet();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const currentChain = getChainInfo(chainId);

  const handleWalletConnect = async (type) => {
    try {
      await connectWallet(type);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Wallet connection failed:', error);
    }
  };

  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setIsCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setIsDropdownOpen(false);
  };

  if (!isConnected) {
    return (
      <div className="relative">
        <Button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          variant="primary"
          size="sm"
          loading={isLoading}
          className="flex items-center space-x-2"
        >
          <WalletIcon className="w-4 h-4" />
          <span>Connect Wallet</span>
          <ChevronDownIcon className="w-4 h-4" />
        </Button>

        {/* Wallet Selection Dropdown */}
        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50">
            <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-medium text-slate-900 dark:text-slate-100">
                Connect Wallet
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                Choose your preferred wallet
              </p>
            </div>

            <div className="py-2">
              {/* MetaMask Option */}
              <button
                onClick={() => handleWalletConnect(WALLET_TYPES.METAMASK)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xs">MM</span>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      MetaMask
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      For BNB Smart Chain
                    </div>
                  </div>
                </div>
              </button>

              {/* Ronin Wallet Option */}
              <button
                onClick={() => handleWalletConnect(WALLET_TYPES.RONIN)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xs">RN</span>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      Ronin Wallet
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      For Ronin Network
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Backdrop */}
        {isDropdownOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsDropdownOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-3 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        {/* Chain Indicator */}
        {currentChain && (
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ backgroundColor: currentChain.color }}
          >
            {currentChain.symbol.slice(0, 2)}
          </div>
        )}

        {/* Address */}
        <div className="hidden sm:flex flex-col items-start">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {formatAddress(address)}
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-400">
            {formatTokenAmount(balance.pfvs)} PFVS
          </div>
        </div>

        <ChevronDownIcon className="w-4 h-4 text-slate-400" />
      </button>

      {/* Account Dropdown */}
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50">
          {/* Account Info */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-slate-900 dark:text-slate-100">
                Account
              </h3>
              <div className="flex items-center space-x-1 text-xs text-slate-500 dark:text-slate-400">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: currentChain?.color }}
                />
                <span>{currentChain?.name}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <WalletIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-sm text-slate-900 dark:text-slate-100">
                    {formatAddress(address, 8, 6)}
                  </span>
                  <button
                    onClick={handleCopyAddress}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                  >
                    {isCopied ? (
                      <CheckIcon className="w-4 h-4 text-success-500" />
                    ) : (
                      <DocumentDuplicateIcon className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  Connected with {walletType === WALLET_TYPES.RONIN ? 'Ronin' : 'MetaMask'}
                </div>
              </div>
            </div>
          </div>

          {/* Balances */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
              Balances
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {currentChain?.nativeCurrency.symbol}
                </span>
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {formatTokenAmount(balance.native, 18, 4)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  PFVS
                </span>
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {formatTokenAmount(balance.pfvs)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-4 py-2">
            <button
              onClick={handleDisconnect}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              <span>Disconnect</span>
            </button>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default WalletConnection; 